import { useState } from "react";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, Trophy } from "lucide-react";
import { PERSONA_ADS, PERSONA_LABELS } from "../../data/personaContent";
import { useUrlAnalytics } from "./AdminUrlAnalyticsTab";

const copy = (text, label) => {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} skopírované`));
};

const AdCard = ({ ad, stats, isWinner }) => {
  const fullUrl = `${window.location.origin}${ad.url}`;
  return (
    <div className={`bg-white border rounded-lg overflow-hidden flex flex-col ${isWinner ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200"}`} data-testid={`persona-ad-${ad.id}`}>
      <div className="relative">
        <img src={ad.image} alt={ad.headline} className="w-full aspect-square object-cover" />
        {isWinner && <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-bold bg-emerald-500 text-white rounded"><Trophy className="w-3 h-3" /> Víťaz</span>}
        <span className="absolute top-2 right-2 px-2 py-1 text-xs font-bold bg-black/70 text-white rounded">{PERSONA_LABELS[ad.persona]}</span>
      </div>
      <div className="p-4 space-y-2 flex-1 text-sm">
        <p className="text-gray-800 leading-relaxed">{ad.primaryText}</p>
        <p className="font-bold text-gray-900">{ad.headline}</p>
        <p className="text-gray-500 text-xs">{ad.description} · Tlačidlo: {ad.cta}</p>
        <div className="grid grid-cols-3 gap-2 pt-2 text-center" data-testid={`persona-ad-stats-${ad.id}`}>
          <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500 uppercase">Návštevy</p><p className="font-bold">{stats.unique_visitors}</p></div>
          <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500 uppercase">Kliky</p><p className="font-bold">{stats.unique_clickers}</p></div>
          <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500 uppercase">Konverzia</p><p className="font-bold">{stats.cvr_pct} %</p></div>
        </div>
      </div>
      <div className="p-3 border-t border-gray-100 flex flex-wrap gap-2">
        <button onClick={() => copy(`${ad.primaryText}\n\n${ad.headline}\n${ad.description}`, "Text")} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold border border-gray-300 rounded hover:bg-gray-50" data-testid={`persona-ad-copy-text-${ad.id}`}><Copy className="w-3 h-3" /> Text</button>
        <button onClick={() => copy(fullUrl, "Odkaz")} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold border border-gray-300 rounded hover:bg-gray-50" data-testid={`persona-ad-copy-url-${ad.id}`}><Copy className="w-3 h-3" /> Odkaz</button>
        <a href={ad.image} download target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold border border-gray-300 rounded hover:bg-gray-50" data-testid={`persona-ad-download-${ad.id}`}><Download className="w-3 h-3" /> Obrázok</a>
        <a href={ad.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-blue-600 hover:underline" data-testid={`persona-ad-open-${ad.id}`}><ExternalLink className="w-3 h-3" /> Stránka</a>
      </div>
    </div>
  );
};

const EMPTY = { unique_visitors: 0, unique_clickers: 0, cvr_pct: 0 };

export default function AdminPersonaAdsTab() {
  const [audience, setAudience] = useState("agentury");
  const { data } = useUrlAnalytics("/pre-", 30);
  const byPage = Object.fromEntries((data?.rows || []).map((r) => [r.page, r]));
  const ads = PERSONA_ADS.filter((a) => a.audience === audience);
  const statsFor = (ad) => byPage[ad.url.split("?")[0]] || EMPTY;
  const best = ads.reduce((w, a) => (statsFor(a).cvr_pct > (w ? statsFor(w).cvr_pct : 0) ? a : w), null);

  return (
    <div className="space-y-5" data-testid="admin-persona-ads-tab">
      <div className="flex flex-wrap items-center gap-2">
        {[["agentury", "Pre agentúry"], ["firmy", "Pre firmy"]].map(([k, l]) => (
          <button key={k} onClick={() => setAudience(k)} data-testid={`persona-ads-audience-${k}`} className={`px-3 py-1.5 text-xs font-bold rounded-full border ${audience === k ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300"}`}>{l}</button>
        ))}
        <p className="text-xs text-gray-500 ml-2">Štatistiky za 30 dní. Každá reklama vedie na vlastnú verziu stránky, víťaz má najvyššiu konverziu.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {ads.map((ad) => <AdCard key={ad.id} ad={ad} stats={statsFor(ad)} isWinner={best?.id === ad.id} />)}
      </div>
    </div>
  );
}
